import { useCallback, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import type { Query } from "@cubejs-client/core"
import { toast } from "react-toastify"
import {
  ArrowDownTrayIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"

import {
  useDeleteCompanyCustomReportMutation,
  useGetCompanyCustomReportQuery,
} from "~/ui-rtk/api/companyCustomReportApi"
import Report, {
  type TReportHandle,
} from "~/ui-rtk/pages/BrandMedia/CreativeTaxonomyReport/components/Report"
import { deriveReportConfig } from "./utils/derive-report"
import SlackNotificationSection from "./components/SlackNotificationSection"
import {
  Button,
  DateRangePicker,
  GranularitySelector,
} from "~/ui-rtk/components/ui/controls"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { H1, H3 } from "~/ui-rtk/components/ui/typography"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { useMetricDictionary } from "~/ui-rtk/hooks/metric-dictionary"
import {
  ConvertNumberTypeArr,
  type ConvertNumberType,
} from "~/ui-rtk/utils/number-utils"
import useDate from "~/ui-rtk/hooks/date"
import { brandRoutes } from "~/ui-rtk/constants/routes"

const dayjs = useDate()
const maxDateRestriction = dayjs().toDate()

export default function ViewCustomReportPage() {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const { data: report, isLoading, error } = useGetCompanyCustomReportQuery(id)

  const { dateRange, compareRange } = useMinMaxDateRange(
    undefined,
    maxDateRestriction,
    AggregationType.DAILY,
  )
  const { setPeriod, setComparePeriod, granularity, setGranularity } =
    usePeriod()

  const [deleteReport, { isLoading: deleting }] =
    useDeleteCompanyCustomReportMutation()
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const handleDelete = async () => {
    try {
      await deleteReport(id).unwrap()
      toast.success("Report deleted")
      navigate(brandRoutes.CUSTOM_REPORTS.NEW)
    } catch {
      toast.error("Failed to delete the report. Please try again.")
    } finally {
      setConfirmingDelete(false)
    }
  }

  // Resolve each measure's display format from the metric dictionary so the
  // viewer formats values per-measure (currency/percent/…) — self-describing,
  // no need to persist formats with the report.
  const { metricMap } = useMetricDictionary()
  const resolveFormat = useCallback(
    (measure: string): ConvertNumberType | undefined => {
      const short = measure.split(".").slice(1).join(".")
      const fmt = metricMap.get(short)?.format ?? metricMap.get(measure)?.format
      return fmt && ConvertNumberTypeArr.includes(fmt as ConvertNumberType)
        ? (fmt as ConvertNumberType)
        : undefined
    },
    [metricMap],
  )

  const changePeriod = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )
    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  const sections = useMemo(
    () =>
      [...(report?.reports?.reports ?? [])].sort((a, b) => a.order - b.order),
    [report],
  )

  const reportRefs = useRef<(TReportHandle | null)[]>([])

  // Render sections in a sliding window (≈3 concurrent) so N sections don't fire
  // N simultaneous Cube queries on load. Each section bumps the window when it
  // settles (see onSettled below).
  const CONCURRENCY = 3
  const [visibleCount, setVisibleCount] = useState(CONCURRENCY)

  const handleDownloadAll = () => {
    // Force every section to mount, then download each as soon as it has data
    // (polling the imperative handle; staggered naturally as queries resolve).
    setVisibleCount(sections.length)
    const done = new Set<number>()
    let attempts = 0
    const tick = () => {
      sections.forEach((_, idx) => {
        if (done.has(idx)) return
        const ref = reportRefs.current[idx]
        if (ref?.hasData()) {
          ref.downloadCsv()
          done.add(idx)
        }
      })
      attempts += 1
      if (done.size < sections.length && attempts < 40) {
        setTimeout(tick, 400)
      }
    }
    setTimeout(tick, 100)
  }

  const rangeReady =
    dateRange?.[0] &&
    dateRange?.[1] &&
    isValidDate(dateRange[0]) &&
    isValidDate(dateRange[1])

  const compareReady = Boolean(
    compareRange?.[0] &&
      compareRange?.[1] &&
      isValidDate(compareRange[0]) &&
      isValidDate(compareRange[1]),
  )

  if (isLoading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Loader size={50} />
      </div>
    )
  }
  if (error || !report) {
    return (
      <div className="px-system-md mt-10 text-system-gray-400">
        Report not found.
      </div>
    )
  }

  return (
    <div className="custom-report-view px-system-md pb-system-2xl">
      <div className="mt-6 flex justify-between items-center gap-system-sm">
        <H1 className="text-white font-bold text-2xl">{report.name}</H1>
        <div className="flex items-center gap-system-sm">
          <Button
            size="sm"
            variant={{ variant: "outlined" }}
            onClick={handleDownloadAll}
            disabled={!sections.length}
          >
            <span className="flex items-center gap-2">
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download all
            </span>
          </Button>
          <Button
            size="sm"
            variant={{ variant: "outlined" }}
            onClick={() =>
              navigate(
                brandRoutes.CUSTOM_REPORTS.EDIT.replace(":id", report.id),
              )
            }
          >
            <span className="flex items-center gap-2">
              <PencilSquareIcon className="w-4 h-4" />
              Edit
            </span>
          </Button>
          <Button
            size="sm"
            variant={{ variant: "outlined" }}
            onClick={() => setConfirmingDelete(true)}
          >
            <span className="flex items-center gap-2">
              <TrashIcon className="w-4 h-4 text-error" />
              Delete
            </span>
          </Button>
          <GranularitySelector value={granularity} onChange={setGranularity} />
          {rangeReady && (
            <DateRangePicker
              start={dateRange[0]}
              end={dateRange[1]}
              compareStart={
                compareRange?.[0] && isValidDate(compareRange[0])
                  ? compareRange[0]
                  : undefined
              }
              compareEnd={
                compareRange?.[1] && isValidDate(compareRange[1])
                  ? compareRange[1]
                  : undefined
              }
              comparisonEnabled={true}
              maxDate={maxDateRestriction}
              onChange={changePeriod}
            />
          )}
        </div>
      </div>

      {rangeReady && (
        <div className="mt-system-xl flex flex-col gap-system-2xl">
          {sections.map((section, idx) => {
            if (idx >= visibleCount) return null
            const { columns, chart } = deriveReportConfig(
              section.query as Query,
              resolveFormat,
            )
            return (
              <Report
                key={`${report.id}-${section.order}`}
                ref={el => {
                  reportRefs.current[idx] = el
                }}
                title={section.name}
                query={section.query as Query}
                chart={chart}
                columns={columns}
                chartId={`custom-${report.id}-${section.order}`}
                filenameBase={`${report.name}-${section.name}`}
                dateRange={dateRange as [Date, Date]}
                compareRange={
                  compareReady ? (compareRange as [Date, Date]) : undefined
                }
                comparisonEnabled={compareReady}
                granularity={granularity}
                chartType={section.chartType}
                onSettled={() =>
                  setVisibleCount(c => Math.max(c, idx + 1 + CONCURRENCY))
                }
              />
            )
          })}
          {!sections.length && (
            <div className="text-system-gray-400">
              This report has no sections yet.
            </div>
          )}
        </div>
      )}

      <SlackNotificationSection reportId={report.id} />

      <Dialog
        isOpen={confirmingDelete}
        handleClose={() => setConfirmingDelete(false)}
        width={420}
      >
        <Card
          rounded
          className="p-5 bg-basic-dark-blue border border-basic-blue text-left"
        >
          <H3 className="text-white font-semibold text-lg">
            Delete “{report.name}”?
          </H3>
          <p className="text-sm text-system-gray-300 mt-2">
            This report will be removed for everyone in your company. This
            action cannot be undone.
          </p>
          <div className="mt-5 flex justify-end gap-system-sm">
            <Button
              size="sm"
              variant={{ variant: "outlined" }}
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant={{ variant: "solid", color: "pink" }}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete report"}
            </Button>
          </div>
        </Card>
      </Dialog>
    </div>
  )
}
